import type { Cuestionario, ValorDeRespuesta } from './api.js';

/**
 * Cuánto lleva respondido, y dónde falta.
 *
 * VIVE FUERA DEL COMPONENTE PARA PODERLO PROBAR. Las pruebas del proyecto corren
 * bajo la resolución de módulos del backend (NodeNext, con extensión `.js` en
 * los imports) y esta web compila con `bundler`, que no la lleva. Este archivo
 * puede leerlo cualquiera de los dos porque lo único que importa es un
 * `import type`, y eso desaparece al compilar. Es el mismo recurso que usa
 * `panel/src/clinico.ts`.
 */

export interface RespuestaEnPantalla {
  preguntaId: string;
  valor: ValorDeRespuesta;
}

export interface Progreso {
  /** Obligatorias que el servidor tiene guardadas. */
  hechas: number;
  /** Obligatorias que tiene el cuestionario. */
  total: number;
}

/**
 * Cuenta lo respondido sobre el cuestionario entero.
 *
 * NO CUENTA LO QUE NO LLEGÓ A GUARDARSE. Una respuesta cuya escritura falló
 * sigue marcada en pantalla —es lo que la persona eligió y sería absurdo
 * borrárselo—, pero el servidor no la tiene, así que sumarla haría que la barra
 * dijera «75/75» justo antes de que «Terminar» rebotara por respuestas que
 * faltan. La barra tiene que contar lo que hay al otro lado, no lo que se ve.
 *
 * «Respondida» es tener valor, sin mirar cuál: la misma regla que aplica el
 * servidor en `progresoDe`, donde se cuentan identificadores y no contenido.
 */
export function contarProgreso(
  cuestionario: Cuestionario,
  respuestas: readonly RespuestaEnPantalla[],
  sinGuardar: ReadonlySet<string> = new Set<string>(),
): Progreso {
  const dadas = new Set(respuestas.map((r) => r.preguntaId).filter((id) => !sinGuardar.has(id)));

  let hechas = 0;
  let total = 0;

  for (const grupo of cuestionario.grupos) {
    for (const pregunta of grupo.preguntas) {
      if (!pregunta.obligatoria) continue;
      total += 1;
      if (dadas.has(pregunta.id)) hechas += 1;
    }
  }

  return { hechas, total };
}

/**
 * En qué parte está la primera de esas preguntas.
 *
 * Se usa para volver atrás cuando el servidor rechaza el cierre por respuestas
 * que faltan: él dice cuáles son y esto dice dónde están. Si ninguna aparece en
 * el cuestionario —contenido cambiado bajo los pies, o una lista vacía— devuelve
 * la primera parte, que es un sitio del que siempre se puede seguir.
 */
export function primerGrupoConAlgunaDe(
  cuestionario: Cuestionario,
  preguntaIds: readonly string[],
): number {
  if (preguntaIds.length === 0) return 0;
  const buscadas = new Set(preguntaIds);

  const indice = cuestionario.grupos.findIndex((grupo) =>
    grupo.preguntas.some((pregunta) => buscadas.has(pregunta.id)),
  );

  return indice === -1 ? 0 : indice;
}
